import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import Actions from "./Actions";
import { ToggleDrawerButton, useLayoutContext } from "./MainLayout";

import Avatar from "@mui/material/Avatar";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";

import { CloseOutlined } from "@mui/icons-material";
import { IconButton, Skeleton, useMediaQuery, useTheme } from "@mui/material";
import { debounce, isEmpty } from "lodash";
import { useUserInfo } from "../utils/security";
import { useUserMenu } from "./Security";

const ListDetailContext = createContext({});

export const useListDetailView = ({ fetchDetail, fetchList, fetchOverview, stayOnOverview }) => {
    const { id: detailId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const theme = useTheme();
    const isFull = !!useMediaQuery(theme.breakpoints.up("lg"));

    const [filter, setFilterState] = useState({});
    const [listLoading, setListLoading] = useState(!!fetchList);
    const [list, setList] = useState({});
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState();
    const [overview, setOverview] = useState();
    const [overviewLoading, setOverviewLoading] = useState(false);

    //TODO use user info?
    const userInfo = useUserInfo();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const setFilter = useCallback(
        debounce(
            (filter) => {
                setFilterState(filter || {});
            },
            1000,
            { trailing: true }
        ),
        [setFilterState]
    );

    const selectItem = useCallback(
        (item) => {
            if (!item) {
                setDetail(undefined);
                if (!!detailId) {
                    navigate(location.pathname.replace(`${detailId}`, ""));
                }
                return;
            }

            setDetail(item);
            if (!!detailId) {
                navigate(location.pathname.replace(`${detailId}`, `${item.id}`));
                return;
            }
            navigate(`${item.id}`);
        },
        [navigate, location, detailId, setDetail]
    );

    const refreshList = useCallback(async () => {
        if (!fetchList) {
            setList({});
            return;
        }
        setListLoading(true);
        try {
            let fetched = await fetchList({ filter, setList, userInfo, list });
            setList(fetched);
        } finally {
            setListLoading(false);
        }
    }, [filter, setList, setListLoading, fetchList, userInfo]);

    //refetch list
    useEffect(() => {
        refreshList();
    }, [refreshList]);

    const refreshDetail = useCallback(async () => {
        const promises = [];

        if (fetchDetail) {
            if (detailId) {
                promises.push(async () => {
                    setDetailLoading(true);
                    try {
                        let detail = await fetchDetail({
                            id: detailId,
                            setDetail,
                            filter,
                            userInfo,
                        });
                        setDetail(detail);
                    } catch (e) {
                        console.error(e);
                    } finally {
                        setDetailLoading(false);
                    }
                });
            }
        }

        if (fetchOverview) {
            promises.push(async () => {
                setOverviewLoading(true);
                try {
                    let overview = await fetchOverview({ filter, setOverview, userInfo });
                    setOverview(overview);
                } catch (e) {
                    console.error(e);
                } finally {
                    setOverviewLoading(false);
                }
            });
        }
        await Promise.allSettled(promises.map((p) => p()));
    }, [
        setOverviewLoading,
        setDetailLoading,
        setDetail,
        fetchDetail,
        detailId,
        filter,
        setOverview,
        fetchOverview,
        userInfo,
    ]);

    //fetch detail or overview
    useEffect(() => {
        refreshDetail();
    }, [refreshDetail]);

    return {
        isFull,
        listLoading,
        list,
        setList,
        selectItem,
        detailId,
        filter,
        setFilter,
        refreshList,
        refreshDetail,
        detail,
        setDetail,
        detailLoading,
        overview,
        overviewLoading,
        setOverview,
        fetchDetail,
        fetchList,
        fetchOverview,
    };
};

export const withListDetail = (Component, options) => {
    return ({ ...props }) => {
        const value = useListDetailView(options || {});
        return (
            <ListDetailContext.Provider value={value}>
                <Component {...props} {...value} />
            </ListDetailContext.Provider>
        );
    };
};

export const useListDetailContext = () => {
    return useContext(ListDetailContext);
};

const ListDetailView = ({ renderList, renderDetail, renderOverview }) => {
    const {
        isFull,
        list,
        listLoading,
        detailId,
        filter,
        setFilter,
        detail,
        detailLoading,
        overview,
        overviewLoading,
        selectItem,
    } = useContext(ListDetailContext);

    //scroll to content top when active item changes
    const contentRef = useRef();
    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTo(0, 0);
        }
    }, [contentRef, detailId, detail]);

    let detailContent = null;

    if (!isEmpty(detailId)) {
        detailContent = renderDetail?.({
            detailId,
            data: detail || {},
            compact: !isFull,
            loading: detailLoading,
            contentRef: contentRef,
            setDetail: selectItem,
        });
    } else if (isFull) {
        detailContent = renderOverview?.({
            data: overview || {},
            compact: !isFull,
            loading: overviewLoading,
            contentRef: contentRef,
        });
    }

    return (
        <>
            {/* Layout */}
            <Box
                sx={(theme) => ({
                    flex: "1 1 auto",
                    display: "flex",
                    flexDirection: "row",
                    minHeight: 0,
                })}>
                {(isEmpty(detailId) || isFull) && (
                    <Box
                        sx={() => ({
                            flex: "0 0 auto",
                            flexBasis: isFull ? 460 : "100%",
                            display: "flex",
                            flexDirection: "column",
                            minHeight: 0,
                            maxWidth: "100%",
                            overflow: "hidden",
                        })}>
                        {renderList?.({
                            filter,
                            setFilter,
                            compact: !isFull,
                            loading: listLoading,
                            data: list || {},
                            overview: isFull ? undefined : overview,
                            selectItem,
                            detailId,
                        })}
                    </Box>
                )}

                {(!isEmpty(detailId) || isFull) && (
                    <Box
                        sx={() => ({
                            flex: "1 1 auto",
                            minHeight: 0,
                            overflow: "hidden",
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            padding: 0,
                        })}>
                        {detailContent}
                    </Box>
                )}
            </Box>
        </>
    );
};

export default ListDetailView;

export const ListDetailViewSectionLayout = ({ children, loading }) => {
    return (
        <>
            <Box
                sx={{
                    display: loading ? "block" : "hidden",
                    flex: 0,
                }}>
                <LinearProgress />
            </Box>
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    minHeight: 0,
                }}>
                {children}
            </Box>
        </>
    );
};

export const ListDetailViewSectionHeader = ({
    onBack,
    title,
    titleNode,
    titleLoading,
    actions,
    role,
    children,
    loading,
    compact,
}) => {
    const userMenu = useUserMenu();
    const { selectItem } = useContext(ListDetailContext);
    const { railVisible } = useLayoutContext();

    const actionItems = [...(actions || [])];

    if (role === "detail") {
        actionItems.push({
            icon: <CloseOutlined />,
            onClick: () => selectItem?.(),
            title: "Zpět na přehled",
            alwaysVisible: true,
        });
    }

    if (role !== "list" || compact) {
        actionItems.push(userMenu);
    }

    return (
        <Box sx={{ flex: "0 0 auto" }}>
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                {(role === "list" || compact) && railVisible !== true && (
                    <Box sx={{ flex: 0 }}>
                        <ToggleDrawerButton />
                    </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", flex: 1, px: 2 }}>
                    {!!onBack && (
                        <IconButton onClick={onBack}>
                            <ArrowBackOutlinedIcon />
                        </IconButton>
                    )}
                    {titleLoading ? (
                        <Skeleton height={62} width={140} />
                    ) : (
                        <Typography
                            color="inherit"
                            sx={() => ({
                                fontWeight: 500,
                                letterSpacing: 0.5,
                                fontSize: 20,
                                opacity: 0.8,
                                py: 2,
                            })}>
                            {title}
                        </Typography>
                    )}
                    {isEmpty(titleNode) ? undefined : (
                        <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 2, px: 2 }}>
                            {titleNode}
                        </Box>
                    )}
                </Box>
                <Box sx={{ flex: 0, whiteSpace: "nowrap" }}>
                    <Actions actions={actionItems} disabled={loading} />
                </Box>
            </Box>
            <Box sx={{ px: compact ? 2 : 0 }}>{children}</Box>
        </Box>
    );
};

export const ListDetailViewSectionContent = ({ children }) => {
    return (
        <Box sx={{ flex: "1 1 auto", overflowY: "auto", minHeight: 0 }}>
            {/*<Container maxWidth="xl" sx={{ px: 2 }}>*/}
            {children}
            {/*</Container>*/}
        </Box>
    );
};

export const ListDetailItemList = ({
    data,
    renderItem,
    compact,
    loading,
    detailId,
    selectItem,
}) => {
    if (loading) {
        return [1, 2, 3, 4, 5].map((idx) => (
            <Box key={idx} sx={{ p: 2 }}>
                <Skeleton variant="rectangular" height={50} sx={{ width: "100%" }} />
            </Box>
        ));
    }
    return (data?.items || []).map((data, idx) => (
        <Box key={idx}>
            {renderItem({
                onClick: () => {
                    selectItem?.(data);
                },
                selected: detailId === data.id,
                data,
                loading,
            })}
        </Box>
    ));
};

export const ListViewItem = ({
    onClick,
    selected,
    mainText,
    secondaryText,
    mainValue,
    secondaryValue,
    avatar,
}) => {
    const theme = useTheme();
    return (
        <Box
            sx={(theme) => ({
                display: "flex",
                padding: `${theme.spacing(1.75)} ${theme.spacing(2)}`,
                gap: theme.spacing(2),
                cursor: "pointer",

                color: !!selected
                    ? theme.palette.onSecondaryContainer.main
                    : theme.palette.onSurfaceVariant.main,
                backgroundColor: !!selected ? theme.palette.secondaryContainer.main : "none",
                borderRadius: theme.spacing(1.75),

                "&:hover": {
                    backgroundColor: theme.palette.surface.main,
                    color: theme.palette.onSurfaceVariant.main,
                },
            })}
            onClick={onClick}>
            {!!avatar && (
                <Box sx={{ flex: "0 0 auto" }}>
                    <Badge
                        overlap="circular"
                        variant="dot"
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
                        <Avatar
                            sx={{
                                backgroundColor: !!selected
                                    ? theme.palette.inversePrimary.main
                                    : theme.palette.outlineVariant.main,
                            }}
                            alt="Al"
                        />
                    </Badge>
                </Box>
            )}
            <Box sx={{ flex: "1 1 auto" }}>
                <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: "1 1 auto" }}>
                        {!!mainText && (
                            <Typography variant="body2" component="div">
                                {mainText}
                            </Typography>
                        )}
                    </Box>
                    {(!!mainValue || mainValue === 0) && (
                        <Box sx={{ flex: "0 0 auto" }}>
                            <Typography variant="body2" component="div">
                                {mainValue}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                    <Box sx={{ flex: "1 1 auto", minWidth: 0, overflowX: "hidden" }}>
                        {!!secondaryText && (
                            <Typography variant="caption" component="div">
                                {Array.isArray(secondaryText)
                                    ? secondaryText.join(" • ")
                                    : secondaryText}
                            </Typography>
                        )}
                    </Box>
                    {(!!secondaryValue || secondaryValue === 0) && (
                        <Box sx={{ flex: "0 0 auto" }}>
                            <Typography variant="caption" component="div">
                                {secondaryValue}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
